import React, { useState, useEffect } from 'react';
import {
  getPracticeInfo,
  updatePracticePaymentsSettings,
  syncLedgerPaymentTypes,
} from '../services/paymentsService';

// Card brands we surface in the ledger brand→payment-type map. Kept in sync
// with the backend's _BRAND_DETECT_PATTERNS / _normalize_card_brand.
const LEDGER_CARD_BRANDS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];

// Surcharge fields are surfaced read-only for now — practices can see
// what is configured but cannot self-serve changes from this tab.
// Edits go through ops (the underlying PUT endpoint still accepts the
// fields, so flipping this flag back to ``false`` re-enables the
// inline editor without any other code changes). Applied to every
// interactive surcharge control below + a banner at the top of the
// section so staff understand why the form looks locked.
const SURCHARGE_FIELDS_READONLY = true;

/**
 * SettingsTab
 * ===========
 *
 * Practice-level configuration that doesn't belong to the Rainforest
 * merchant onboarding flow. Two distinct sections:
 *   1. Payment Settings — card-on-file toggles (auto-charge + DOB gate).
 *   2. Surcharge Settings — credit-card surcharging opt-in plus the
 *      legal/state metadata we need to compute and disclose it.
 *
 * Both sections persist to ``practice.payments`` in Cosmos via
 * ``PUT /api/payments/practices/<id>/payments-settings``.
 */
const SettingsTab = ({ practiceId }) => {
  const [paymentsSettings, setPaymentsSettings] = useState({
    autoChargeOnFileEnabled: false,
    // Auto-cancel a pending invoice once the patient pays it directly in
    // the PMS (Dentrix). Default OFF — opt-in per practice.
    autoCancelPaidInvoices: false,
    requireDobToPay: false,
    // Auto-post captured payments to the PMS ledger, and *when* to do
    // it. Timing is only meaningful while the toggle is on; it still
    // persists so flipping the toggle back on restores the choice.
    autoPostLedgerEnabled: false,
    autoPostLedgerTiming: 'processing',
    // Also post insurance invoices, as an insurance payment on the linked
    // claim. Only meaningful while autoPostLedgerEnabled is on.
    autoPostInsuranceEnabled: false,
    autoPostInsuranceSupported: true,
    surchargeEnabled: false,
    surchargePctCap: 3,
    surchargeRegisteredState: '',
    surchargeDisclosure: '',
    // null = "use server default" (DEFAULT_DEBIT_CARD_BILLING_PCT,
    // surfaced via debitCardBillingPctDefault below). A real number
    // means the practice has explicitly opted into a specific rate.
    debitCardBillingPct: null,
    // Alternative payment method opt-ins (default OFF — practice
    // must explicitly accept the higher PayPal-network rates). When
    // surchargingEnabled is also on, these are hidden from the
    // patient regardless of the toggle state because PayPal forbids
    // surcharging PayPal. The UI below greys the toggles in that
    // case so staff understand the gating.
    paypalWalletEnabled: false,
    venmoEnabled: false,
    paypalPayLaterEnabled: false,
  });
  // Server tells us its default; we mirror it here so the input
  // placeholder always reflects what'd actually be used if the
  // practice doesn't override.
  const [debitCardBillingPctDefault, setDebitCardBillingPctDefault] = useState(0.5);
  // Hard-blocked states are returned from the server (single source of
  // truth — we don't want a stale frontend list silently drifting from
  // the server-side compute_surcharge enforcement).
  const [hardBlockedStates, setHardBlockedStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // PMS ledger payment types (synced from Zatanna). ``ledgerTypes`` is the
  // cached [{id, description}] list; ``ledgerDefaultTypeId`` is the single
  // fallback the admin picks; ``cardTypeMap`` is the derived brand→id map.
  const [ledgerTypes, setLedgerTypes] = useState([]);
  const [ledgerDefaultTypeId, setLedgerDefaultTypeId] = useState('');
  const [cardTypeMap, setCardTypeMap] = useState({});
  const [ledgerTypesSyncedAt, setLedgerTypesSyncedAt] = useState(null);
  const [syncingTypes, setSyncingTypes] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  // Ledger provider attribution. ``ledgerProviders`` is the pickable
  // [{id, name}] list from the practice doc; ``ledgerDefaultProviderId`` is
  // the admin-picked provider used as the last-resort attribution fallback.
  const [ledgerProviders, setLedgerProviders] = useState([]);
  const [ledgerDefaultProviderId, setLedgerDefaultProviderId] = useState('');
  // Local-only edit buffer for the free-text surcharge fields so we
  // don't fire a PUT on every keystroke. Committed onBlur (and via
  // handleSurchargeFieldCommit below).
  const [surchargeDraft, setSurchargeDraft] = useState({
    surchargePctCap: '3',
    surchargeRegisteredState: '',
    surchargeDisclosure: '',
    // Empty string = "I haven't typed anything", which we treat as
    // "use the default" on commit (sends null to the server). A real
    // number is the explicit override.
    debitCardBillingPct: '',
  });

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getPracticeInfo(practiceId);
        if (cancelled) return;
        const incoming = data?.practice?.payments;
        if (incoming) {
          const hydrated = {
            autoChargeOnFileEnabled: !!incoming.autoChargeOnFileEnabled,
            autoCancelPaidInvoices: !!incoming.autoCancelPaidInvoices,
            requireDobToPay: !!incoming.requireDobToPay,
            autoPostLedgerEnabled: !!incoming.autoPostLedgerEnabled,
            autoPostLedgerTiming: (
              incoming.autoPostLedgerTiming === 'succeeded' ? 'succeeded' : 'processing'
            ),
            autoPostInsuranceEnabled: !!incoming.autoPostInsuranceEnabled,
            autoPostInsuranceSupported: incoming.autoPostInsuranceSupported !== false,
            surchargeEnabled: !!incoming.surchargeEnabled,
            surchargePctCap: Number(incoming.surchargePctCap || 3),
            surchargeRegisteredState: (incoming.surchargeRegisteredState || '').toUpperCase(),
            surchargeDisclosure: incoming.surchargeDisclosure || '',
            debitCardBillingPct: (
              incoming.debitCardBillingPct == null
                ? null
                : Number(incoming.debitCardBillingPct)
            ),
            paypalWalletEnabled: !!incoming.paypalWalletEnabled,
            venmoEnabled: !!incoming.venmoEnabled,
            paypalPayLaterEnabled: !!incoming.paypalPayLaterEnabled,
          };
          setPaymentsSettings(hydrated);
          setSurchargeDraft({
            surchargePctCap: String(hydrated.surchargePctCap),
            surchargeRegisteredState: hydrated.surchargeRegisteredState,
            surchargeDisclosure: hydrated.surchargeDisclosure,
            debitCardBillingPct: (
              hydrated.debitCardBillingPct == null
                ? ''
                : String(hydrated.debitCardBillingPct)
            ),
          });
          if (typeof incoming.debitCardBillingPctDefault === 'number') {
            setDebitCardBillingPctDefault(incoming.debitCardBillingPctDefault);
          }
          if (Array.isArray(incoming.surchargeHardBlockedStates)) {
            setHardBlockedStates(incoming.surchargeHardBlockedStates);
          }
          setLedgerTypes(
            Array.isArray(incoming.ledgerPaymentTypes)
              ? incoming.ledgerPaymentTypes
              : [],
          );
          setLedgerDefaultTypeId(
            incoming.ledgerDefaultPaymentTypeId == null
              ? ''
              : String(incoming.ledgerDefaultPaymentTypeId),
          );
          setCardTypeMap(incoming.ledgerCardPaymentTypeMap || {});
          setLedgerTypesSyncedAt(incoming.ledgerPaymentTypesSyncedAt || null);
          setLedgerProviders(
            Array.isArray(incoming.ledgerProviders)
              ? incoming.ledgerProviders
              : [],
          );
          setLedgerDefaultProviderId(
            incoming.ledgerDefaultProviderId == null
              ? ''
              : String(incoming.ledgerDefaultProviderId),
          );
        }
      } catch (e) {
        // Non-fatal — toggles default to off.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  const togglePaymentsSetting = async (key) => {
    // Client-side preflight: enabling surcharging requires the
    // registered-state field. The server rejects this with a 400 too,
    // but our shared fetch helper throws on non-2xx and swallows the
    // structured body, so checking here gives the user a clean
    // explanation instead of a generic "failed to save" toast.
    const turningOn = key === 'surchargeEnabled' && !paymentsSettings.surchargeEnabled;
    if (turningOn && !paymentsSettings.surchargeRegisteredState) {
      setError(
        'Add the state where this practice is registered before enabling '
        + 'credit-card surcharging.',
      );
      return;
    }

    const prev = paymentsSettings;
    const next = { ...paymentsSettings, [key]: !paymentsSettings[key] };
    setPaymentsSettings(next);
    setSaving(true);
    setError('');
    try {
      await updatePracticePaymentsSettings(practiceId, { [key]: next[key] });
    } catch (e) {
      setPaymentsSettings(prev);
      setError('Failed to save setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Persist the ledger-posting timing (radio choice). Optimistic with
  // rollback, same shape as togglePaymentsSetting but for a string enum.
  const setLedgerTiming = async (value) => {
    if (paymentsSettings.autoPostLedgerTiming === value) return;
    const prev = paymentsSettings;
    setPaymentsSettings({ ...paymentsSettings, autoPostLedgerTiming: value });
    setSaving(true);
    setError('');
    try {
      await updatePracticePaymentsSettings(practiceId, { autoPostLedgerTiming: value });
    } catch (e) {
      setPaymentsSettings(prev);
      setError('Failed to save setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Pull the practice's payment types from the PMS (Zatanna) and cache them
  // on the practice doc. The backend seeds the brand→type map on first sync.
  const handleSyncPaymentTypes = async () => {
    setSyncingTypes(true);
    setError('');
    setSyncMsg('');
    try {
      const res = await syncLedgerPaymentTypes(practiceId);
      setLedgerTypes(Array.isArray(res?.paymentTypes) ? res.paymentTypes : []);
      setCardTypeMap(res?.cardPaymentTypeMap || {});
      setLedgerTypesSyncedAt(res?.syncedAt || null);
      if (res?.defaultPaymentTypeId != null) {
        setLedgerDefaultTypeId(String(res.defaultPaymentTypeId));
      }
      const n = Array.isArray(res?.paymentTypes) ? res.paymentTypes.length : 0;
      setSyncMsg(
        `Synced ${n} payment type${n === 1 ? '' : 's'} from the PMS`
        + (res?.seededCardPaymentTypeMap ? ' · auto-mapped card brands.' : '.'),
      );
    } catch (e) {
      setError(e?.message || 'Could not sync payment types from the PMS.');
    } finally {
      setSyncingTypes(false);
    }
  };

  // Persist the default (fallback) ledger payment type the admin picks.
  const setLedgerDefaultPaymentType = async (value) => {
    const prev = ledgerDefaultTypeId;
    setLedgerDefaultTypeId(value);
    setSaving(true);
    setError('');
    try {
      // Send a number when the id is numeric, else the raw string; '' clears.
      const payload = value === ''
        ? null
        : (Number.isNaN(Number(value)) ? value : Number(value));
      await updatePracticePaymentsSettings(practiceId, {
        ledgerDefaultPaymentTypeId: payload,
      });
    } catch (e) {
      setLedgerDefaultTypeId(prev);
      setError('Failed to save the default payment type. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Human label for a payment-type id, resolved against the synced list.
  const ledgerTypeLabel = (id) => {
    const t = ledgerTypes.find((x) => String(x.id) === String(id));
    return t ? `${t.description || 'Untitled'} (#${t.id})` : `#${id}`;
  };

  // Persist the admin-picked default provider — the last-resort fallback for
  // ledger attribution. '' clears it (writeback then guesses a recent
  // practice provider instead).
  const setLedgerDefaultProvider = async (value) => {
    const prev = ledgerDefaultProviderId;
    setLedgerDefaultProviderId(value);
    setSaving(true);
    setError('');
    try {
      await updatePracticePaymentsSettings(practiceId, {
        ledgerDefaultProviderId: value === '' ? null : value,
      });
    } catch (e) {
      setLedgerDefaultProviderId(prev);
      setError('Failed to save the default provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Commit a single free-text surcharge field. Used onBlur so the user
  // can type freely without us firing a network request per character.
  const handleSurchargeFieldCommit = async (key, rawValue) => {
    let value = rawValue;
    if (key === 'surchargePctCap') {
      const parsed = parseFloat(rawValue);
      if (Number.isNaN(parsed)) {
        setError('Surcharge percentage must be a number.');
        return;
      }
      // Mirror the server's clamp client-side so the user sees the
      // adjusted value immediately.
      value = Math.max(0, Math.min(4, parsed));
    } else if (key === 'surchargeRegisteredState') {
      value = (rawValue || '').toUpperCase();
      if (value && value.length !== 2) {
        setError('Use a 2-letter US state code (e.g. CA, FL, TX).');
        return;
      }
    } else if (key === 'debitCardBillingPct') {
      // Empty string is a real choice — "use the server default".
      // We send null upstream so the server pops the override and
      // falls back to DEFAULT_DEBIT_CARD_BILLING_PCT at quote time.
      const trimmed = (rawValue ?? '').toString().trim();
      if (trimmed === '') {
        value = null;
      } else {
        const parsed = parseFloat(trimmed);
        if (Number.isNaN(parsed)) {
          setError('Debit card billing percentage must be a number or blank.');
          return;
        }
        value = Math.max(0, Math.min(3, parsed));
      }
    }

    if (paymentsSettings[key] === value) return; // nothing to do

    const prev = paymentsSettings;
    const next = { ...paymentsSettings, [key]: value };
    setPaymentsSettings(next);
    setSurchargeDraft((d) => ({ ...d, [key]: String(value ?? '') }));
    setSaving(true);
    setError('');
    try {
      const res = await updatePracticePaymentsSettings(practiceId, { [key]: value });
      // Server clamps further (e.g. brand-cap interactions); echo back
      // the persisted value so what the user sees == what we stored.
      const echoed = res?.payments;
      if (echoed && key in echoed) {
        setPaymentsSettings((p) => ({ ...p, [key]: echoed[key] }));
        setSurchargeDraft((d) => ({ ...d, [key]: String(echoed[key] ?? '') }));
      }
    } catch (e) {
      setPaymentsSettings(prev);
      setSurchargeDraft((d) => ({ ...d, [key]: String(prev[key] ?? '') }));
      setError('Failed to save setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const stateIsHardBlocked = hardBlockedStates.includes(
    (paymentsSettings.surchargeRegisteredState || '').toUpperCase(),
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <h4 style={{ margin: '0 0 4px 0', color: '#2b302d' }}>Payment Settings</h4>
        <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '0.9rem' }}>
          Card-on-file behaviour for invoices created from this practice.
        </p>

        <SettingToggle
          label="Auto-charge default card on file when an invoice is created"
          description="When a patient has saved a default card, new invoices charge it automatically. Staff can still override at invoice creation."
          checked={paymentsSettings.autoChargeOnFileEnabled}
          disabled={saving}
          onChange={() => togglePaymentsSetting('autoChargeOnFileEnabled')}
        />

        <div style={{ height: 12 }} />

        <SettingToggle
          label="Auto-cancel invoices paid in the office (PMS)"
          description="When a pending invoice's balance is paid directly in your PMS (e.g. at the front desk), automatically cancel the Newton invoice so it stops showing as pending. Only cancels on an exact, unambiguous payment match."
          checked={paymentsSettings.autoCancelPaidInvoices}
          disabled={saving}
          onChange={() => togglePaymentsSetting('autoCancelPaidInvoices')}
        />

        <div style={{ height: 12 }} />

        <SettingToggle
          label="Require date of birth on the public invoice page"
          description="Patients must verify their date of birth before they can pay or manage saved cards on the public invoice link."
          checked={paymentsSettings.requireDobToPay}
          disabled={saving}
          onChange={() => togglePaymentsSetting('requireDobToPay')}
        />

        {error && (
          <div style={{ color: '#b42318', fontSize: '0.85rem', marginTop: 8 }}>
            {error}
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------
          Ledger Posting
          ---------------------------------------------------------------
          Auto-write captured Newton payments back to the practice's PMS
          ledger instead of staff hand-posting them. Two pieces of state:
            1. autoPostLedgerEnabled — master on/off.
            2. autoPostLedgerTiming  — when the write fires:
               • "processing" — the moment the payment starts processing
                 (payin.processing).
               • "succeeded"  — once the payment settles/completes
                 (payin.succeeded).
          The timing radios are shown always but disabled while the
          master toggle is off (the choice is still persisted so turning
          the feature back on restores it). Enforcement / the actual
          ledger write lives server-side + in the dispatcher; this tab is
          the settings surface only.
          --------------------------------------------------------- */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <h4 style={{ margin: '0 0 4px 0', color: '#2b302d' }}>Ledger posting</h4>
        <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '0.9rem' }}>
          Automatically write captured payments back to this practice&apos;s
          practice-management-system ledger, instead of posting them by hand.
        </p>

        <SettingToggle
          label="Auto-post payments to the ledger"
          description="When enabled, Newton posts each captured payment to your practice management system ledger automatically."
          checked={paymentsSettings.autoPostLedgerEnabled}
          disabled={saving}
          onChange={() => togglePaymentsSetting('autoPostLedgerEnabled')}
        />

        <div style={{
          marginTop: 16,
          opacity: paymentsSettings.autoPostLedgerEnabled ? 1 : 0.55,
        }}>
          <div style={{
            fontWeight: 500,
            color: '#2b302d',
            fontSize: 14,
            marginBottom: 8,
          }}>
            When should payments post to the ledger?
          </div>

          <LedgerTimingOption
            label="When payment starts"
            description="Post the moment the payment starts processing — before it has settled."
            selected={paymentsSettings.autoPostLedgerTiming === 'processing'}
            disabled={saving || !paymentsSettings.autoPostLedgerEnabled}
            onSelect={() => setLedgerTiming('processing')}
          />

          <div style={{ height: 8 }} />

          <LedgerTimingOption
            label="When payment is settled"
            description="Wait until the payment has settled / completed successfully before posting."
            selected={paymentsSettings.autoPostLedgerTiming === 'succeeded'}
            disabled={saving || !paymentsSettings.autoPostLedgerEnabled}
            onSelect={() => setLedgerTiming('succeeded')}
          />
        </div>

        <div style={{
          marginTop: 16,
          opacity: paymentsSettings.autoPostLedgerEnabled ? 1 : 0.55,
        }}>
          <SettingToggle
            label="Also post insurance invoices"
            description={paymentsSettings.autoPostInsuranceSupported
              ? 'Post payments on insurance invoices as an insurance payment on the claim linked to the invoice. On Open Dental, only when the amount matches the claim\'s insurance estimate; anything else is left for staff to post from the EOB.'
              : 'Not available for Open Dental yet. Post insurance payments in the PMS.'}
            checked={paymentsSettings.autoPostInsuranceEnabled && paymentsSettings.autoPostInsuranceSupported}
            disabled={saving || !paymentsSettings.autoPostLedgerEnabled || !paymentsSettings.autoPostInsuranceSupported}
            onChange={() => togglePaymentsSetting('autoPostInsuranceEnabled')}
          />
        </div>

        {/* -----------------------------------------------------------
            PMS payment types — sync from the PMS, pick a default, and
            review the auto-derived card-brand → payment-type mapping.
            ----------------------------------------------------------- */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eef0ee' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, color: '#2b302d', fontSize: 14 }}>
                PMS payment types
              </div>
              <div style={{ color: '#6b716d', fontSize: '0.85rem', marginTop: 2 }}>
                Pull the payment types from this practice&apos;s PMS so posted
                payments land under the right ledger category.
                {ledgerTypesSyncedAt && (
                  <>
                    {' '}
                    Last synced{' '}
                    {new Date(ledgerTypesSyncedAt).toLocaleString()}.
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleSyncPaymentTypes}
              disabled={syncingTypes}
              title="Refresh payment types from the PMS catalog"
              style={{
                flexShrink: 0,
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid #1f5a3d',
                background: syncingTypes ? '#eef0ee' : '#1f5a3d',
                color: syncingTypes ? '#6b716d' : '#fff',
                fontSize: 13,
                fontWeight: 500,
                cursor: syncingTypes ? 'not-allowed' : 'pointer',
              }}
            >
              {syncingTypes ? 'Syncing…' : 'Sync payment types from PMS'}
            </button>
          </div>

          {syncMsg && (
            <div style={{ color: '#1d6b40', fontSize: '0.85rem', marginTop: 8 }}>
              {syncMsg}
            </div>
          )}

          {ledgerTypes.length > 0 && (
            <>
              {/* Default (fallback) payment type picker. */}
              <div style={{ marginTop: 16 }}>
                <label style={{
                  display: 'block',
                  fontWeight: 500,
                  color: '#2b302d',
                  fontSize: 14,
                  marginBottom: 6,
                }}>
                  Default payment type
                </label>
                <div style={{ color: '#6b716d', fontSize: '0.85rem', marginBottom: 8 }}>
                  Used when the card brand can&apos;t be matched to a specific
                  type below (e.g. an unknown brand).
                </div>
                <select
                  value={ledgerDefaultTypeId}
                  disabled={saving}
                  onChange={(e) => setLedgerDefaultPaymentType(e.target.value)}
                  style={{
                    width: '100%',
                    maxWidth: 420,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #d6dbd7',
                    fontSize: 14,
                    background: '#fff',
                  }}
                >
                  <option value="">— No default (skip when unmatched) —</option>
                  {ledgerTypes.map((t) => (
                    <option key={String(t.id)} value={String(t.id)}>
                      {t.description || 'Untitled'} (#{t.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Auto-derived card-brand → payment-type map (read-only). */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 500, color: '#2b302d', fontSize: 14, marginBottom: 6 }}>
                  Card brand mapping
                </div>
                <div style={{ color: '#6b716d', fontSize: '0.85rem', marginBottom: 8 }}>
                  Auto-derived from the synced types. Each card brand posts
                  under the matched payment type; unmatched brands fall back to
                  the default above.
                </div>
                <div style={{
                  border: '1px solid #eef0ee',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}>
                  {LEDGER_CARD_BRANDS.map((brand, i) => {
                    const id = cardTypeMap[brand];
                    const mapped = id !== undefined && id !== null && id !== '';
                    return (
                      <div
                        key={brand}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          padding: '8px 12px',
                          fontSize: 13,
                          background: i % 2 ? '#f7f8f7' : '#fff',
                        }}
                      >
                        <span style={{ fontWeight: 500, color: '#2b302d' }}>{brand}</span>
                        <span style={{ color: mapped ? '#2b302d' : '#a8520c', textAlign: 'right' }}>
                          {mapped ? ledgerTypeLabel(id) : 'Unmapped → uses default'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Default (fallback) provider picker. Populated from the practice
              doc's providers — independent of the payment-type sync. */}
          {ledgerProviders.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: '#2b302d',
                fontSize: 14,
                marginBottom: 6,
              }}>
                Default provider (fallback)
              </label>
              <div style={{ color: '#6b716d', fontSize: '0.85rem', marginBottom: 8 }}>
                Payments are attributed to the patient&apos;s own provider, then
                the provider they last saw. This is the last-resort fallback
                when neither is known — otherwise the writeback guesses a recent
                practice provider.
              </div>
              <select
                value={ledgerDefaultProviderId}
                disabled={saving}
                onChange={(e) => setLedgerDefaultProvider(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: 420,
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: '1px solid #d6dbd7',
                  fontSize: 14,
                  background: '#fff',
                }}
              >
                <option value="">— No default (guess a recent provider) —</option>
                {ledgerProviders.map((p) => (
                  <option key={String(p.id)} value={String(p.id)}>
                    {p.name} ({p.id})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ---------------------------------------------------------------
          Alternative Payment Methods
          ---------------------------------------------------------------
          Per-practice opt-ins for non-card payment rails. Defaults OFF
          because:
            • PayPal Wallet costs the merchant 2.99% + $0.49 (similar
              to cards but with a higher fixed fee — bad math on small
              invoices).
            • Venmo costs 3.49% + $0.49.
            • Pay Later (BNPL) costs 4.99% + $0.49 — almost 2× a
              regular card transaction. PayPal eats the consumer
              credit risk; merchant always gets paid in full upfront.
          The toggles are disabled (greyed out) when surcharging is on
          because PayPal's merchant agreement forbids surcharging
          PayPal payments regardless of underlying funding source.
          Exposing a fee-free PayPal lane next to a 3%-surcharged card
          lane would defeat the surcharging opt-in. Disclosure is in
          the description copy so staff understand the gating before
          they raise a support ticket asking why their toggles are
          inert.
          --------------------------------------------------------- */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <h4 style={{ margin: '0 0 4px 0', color: '#2b302d' }}>
          Alternative payment methods
        </h4>
        <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '0.9rem' }}>
          Show non-card payment buttons on the public invoice page.
          {paymentsSettings.surchargeEnabled && (
            <>
              {' '}<strong>These rails are hidden from patients while
              credit-card surcharging is enabled</strong> — PayPal's
              merchant agreement forbids surcharging PayPal, so showing
              both side-by-side would let patients route around the
              surcharge.
            </>
          )}
        </p>

        <SettingToggle
          label="Accept PayPal"
          description="Adds a PayPal button to the public invoice page."
          checked={paymentsSettings.paypalWalletEnabled}
          disabled={saving || paymentsSettings.surchargeEnabled}
          onChange={() => togglePaymentsSetting('paypalWalletEnabled')}
        />

        <div style={{ height: 12 }} />

        <SettingToggle
          label="Accept Venmo"
          description="Adds a Venmo button to the public invoice page (US patients only)."
          checked={paymentsSettings.venmoEnabled}
          disabled={saving || paymentsSettings.surchargeEnabled}
          onChange={() => togglePaymentsSetting('venmoEnabled')}
        />

        <div style={{ height: 12 }} />

        <SettingToggle
          label="Accept PayPal Pay Later (Buy Now, Pay Later)"
          description={
            'Adds a PayPal Pay Later button to the public invoice page for invoices between $30 and $10,000. '
            + 'PayPal pays the practice in full upfront and collects from the patient in 4 installments.'
          }
          checked={paymentsSettings.paypalPayLaterEnabled}
          disabled={saving || paymentsSettings.surchargeEnabled}
          onChange={() => togglePaymentsSetting('paypalPayLaterEnabled')}
        />
      </div>

      {/* ---------------------------------------------------------------
          Surcharge Settings
          ---------------------------------------------------------------
          Opt-in credit-card surcharging. Three pieces of state matter:
            1. surchargeRegisteredState — which US state the practice is
               legally registered in. Required before surchargeEnabled
               can be flipped on. Surfaced as a 2-letter code (no
               picker dropdown; staff-facing field).
            2. surchargePctCap — practice-chosen percentage. Server
               clamps to per-brand caps (Visa/MC/Discover = 3.0).
            3. surchargeDisclosure — patient-facing copy shown on the
               public invoice page above the payment form. Optional;
               compute_surcharge falls back to a sensible default.
          Whether the toggle is a no-op (state in hard-blocked list,
          required state empty, etc.) is reflected in disabled/help
          copy below — the actual enforcement still lives server-side.
          --------------------------------------------------------- */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px',
      }}>
        <h4 style={{ margin: '0 0 4px 0', color: '#2b302d' }}>Surcharge Settings</h4>
        <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: '0.9rem' }}>
          Pass credit-card processing fees through to patients on the
          public invoice page. Debit cards, prepaid cards, and ACH are
          never surcharged.
        </p>

        {SURCHARGE_FIELDS_READONLY && (
          <div style={{
            marginBottom: 16,
            padding: '10px 12px',
            backgroundColor: '#eef0ee',
            border: '1px solid #e3e7e4',
            borderRadius: 6,
            color: '#3c4540',
            fontSize: '0.85rem',
          }}>
            These settings are view-only. Contact your Newton account
            manager to change your registered state, surcharge
            percentage, debit billing rate, or disclosure copy.
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="surchargeRegisteredState"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Registered state
          </label>
          <input
            id="surchargeRegisteredState"
            type="text"
            value={surchargeDraft.surchargeRegisteredState}
            onChange={(e) => setSurchargeDraft((d) => ({
              ...d,
              surchargeRegisteredState: e.target.value.toUpperCase().slice(0, 2),
            }))}
            onBlur={(e) => handleSurchargeFieldCommit(
              'surchargeRegisteredState',
              e.target.value,
            )}
            placeholder="CA"
            maxLength={2}
            style={{
              width: 80,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #d6dbd7',
              fontSize: 14,
              textTransform: 'uppercase',
            }}
            disabled={saving || SURCHARGE_FIELDS_READONLY}
            readOnly={SURCHARGE_FIELDS_READONLY}
          />
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Two-letter US state code. Required before surcharging can be enabled.
          </div>
          {stateIsHardBlocked && (
            <div style={{
              marginTop: 8,
              padding: '8px 10px',
              backgroundColor: '#fdf3e3',
              border: '1px solid #f3d9ae',
              borderRadius: 6,
              color: '#a8520c',
              fontSize: '0.8rem',
            }}>
              <strong>Note:</strong> consumer-card surcharging is restricted by
              law in this state. Only business / commercial cards will be
              surcharged for patients here.
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="surchargePctCap"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Surcharge percentage
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              id="surchargePctCap"
              type="number"
              min="0"
              max="4"
              step="0.1"
              value={surchargeDraft.surchargePctCap}
              onChange={(e) => setSurchargeDraft((d) => ({
                ...d,
                surchargePctCap: e.target.value,
              }))}
              onBlur={(e) => handleSurchargeFieldCommit('surchargePctCap', e.target.value)}
              style={{
                width: 90,
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #d6dbd7',
                fontSize: 14,
              }}
              disabled={saving || SURCHARGE_FIELDS_READONLY}
              readOnly={SURCHARGE_FIELDS_READONLY}
            />
            <span style={{ color: '#6b716d', fontSize: 14 }}>%</span>
          </div>
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Capped at 3% for Visa, Mastercard, and Discover regardless of value
            entered. American Express is never surcharged.
          </div>
        </div>

        {/* Debit-card billing override.
            ----------------------------
            Patients with debit cards see no surcharge (debit is excluded
            from compute_surcharge entirely). But by default Rainforest
            still bills the merchant their credit-card profile rate
            (~3%) on those debit transactions, which over-bills the
            merchant since debit interchange is dramatically cheaper.
            This field lets the practice override the platform's billing
            fee on debit transactions only — patient-facing UX is
            identical, only the merchant's net changes. */}
        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="debitCardBillingPct"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Debit card billing rate
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              id="debitCardBillingPct"
              type="number"
              min="0"
              max="3"
              step="0.1"
              value={surchargeDraft.debitCardBillingPct}
              placeholder={String(debitCardBillingPctDefault)}
              onChange={(e) => setSurchargeDraft((d) => ({
                ...d,
                debitCardBillingPct: e.target.value,
              }))}
              onBlur={(e) => handleSurchargeFieldCommit(
                'debitCardBillingPct',
                e.target.value,
              )}
              style={{
                width: 90,
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #d6dbd7',
                fontSize: 14,
              }}
              disabled={saving || SURCHARGE_FIELDS_READONLY}
              readOnly={SURCHARGE_FIELDS_READONLY}
            />
            <span style={{ color: '#6b716d', fontSize: 14 }}>%</span>
          </div>
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Platform billing fee taken from debit-card payments.
            Patients are never shown a surcharge on debit cards — this
            only affects the merchant's net. Leave blank to use the
            default ({debitCardBillingPctDefault}%).
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="surchargeDisclosure"
            style={{
              display: 'block',
              fontWeight: 500,
              color: '#2b302d',
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Disclosure text (shown to patients)
          </label>
          <textarea
            id="surchargeDisclosure"
            rows={3}
            value={surchargeDraft.surchargeDisclosure}
            onChange={(e) => setSurchargeDraft((d) => ({
              ...d,
              surchargeDisclosure: e.target.value.slice(0, 500),
            }))}
            onBlur={(e) => handleSurchargeFieldCommit(
              'surchargeDisclosure',
              e.target.value,
            )}
            placeholder="Leave blank to use our default copy."
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #d6dbd7',
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
            disabled={saving || SURCHARGE_FIELDS_READONLY}
            readOnly={SURCHARGE_FIELDS_READONLY}
          />
          <div style={{ color: '#6b716d', fontSize: '0.8rem', marginTop: 4 }}>
            Appears on the public invoice page above the payment form.
            Max 500 characters.
          </div>
        </div>

        <SettingToggle
          label="Enable credit-card surcharging"
          description={
            paymentsSettings.surchargeEnabled
              ? 'Active. The surcharge is applied only after the patient enters a credit card.'
              : 'Patients pay no surcharge today. Toggle on to start passing card fees through on the public invoice page.'
          }
          checked={paymentsSettings.surchargeEnabled}
          disabled={
            saving
            || !paymentsSettings.surchargeRegisteredState
            || SURCHARGE_FIELDS_READONLY
          }
          onChange={() => togglePaymentsSetting('surchargeEnabled')}
        />
      </div>
    </div>
  );
};

// A single radio-style option for the ledger-posting timing choice.
// Mirrors SettingToggle's layout so the two controls sit consistently
// in the same card.
const LedgerTimingOption = ({ label, description, selected, disabled, onSelect }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
  }}>
    <input
      type="radio"
      name="autoPostLedgerTiming"
      checked={!!selected}
      disabled={disabled}
      onChange={onSelect}
      style={{
        flexShrink: 0,
        width: 16,
        height: 16,
        margin: 0,
        marginTop: 3,
        accentColor: '#1f5a3d',
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 500, color: '#2b302d', lineHeight: 1.4 }}>{label}</div>
      {description && (
        <div style={{ color: '#6b716d', fontSize: '0.85rem', marginTop: 2, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
    </div>
  </label>
);

const SettingToggle = ({ label, description, checked, disabled, onChange }) => (
  <label style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    width: '100%',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    cursor: disabled ? 'wait' : 'pointer',
    opacity: disabled ? 0.7 : 1,
  }}>
    <input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={onChange}
      style={{
        flexShrink: 0,
        width: 16,
        height: 16,
        margin: 0,
        marginTop: 3,
        accentColor: '#1f5a3d',
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 500, color: '#2b302d', lineHeight: 1.4 }}>{label}</div>
      {description && (
        <div style={{ color: '#6b716d', fontSize: '0.85rem', marginTop: 2, lineHeight: 1.4 }}>
          {description}
        </div>
      )}
    </div>
  </label>
);

export default SettingsTab;
